import type {NzbCheckResult} from '@repo/shared/api/website_api';

import {showAlert} from '@repo/shared-web/components/core/notifications';
import {createMapStore} from '@repo/shared-web/lib/map_data_store';

import {apiCall} from '@repo/website-frontend/lib/api';
import {getNzbsu, setNzbsu} from '@repo/website-frontend/stores/nzbsu_store';

export type QueueStatus =
  | {stage: 'queued'; guid: string; cancel: () => void}
  | {stage: 'inprogress'; guid: string; cancel: () => void}
  | {stage: 'failed'; guid: string; err: unknown}
  | {stage: 'success'; guid: string; checkResult: NzbCheckResult};

const checkingQueueStore = createMapStore<string, QueueStatus>();
export const useCheckingQueue = checkingQueueStore.useData;
const setCheckingQueue = checkingQueueStore.setData;
export const getCheckingQueue = checkingQueueStore.getData;

const BUSY_ERROR_MESSAGE = 'Another NZB check is running';
const BUSY_ALERT_COOLDOWN_MS = 60 * 1000;

const internalQueue: string[] = [];

let inProgress: string | undefined;
let inProgressCanceled: string | undefined;
let lastBusyAlertAt = 0;

function processQueue(): void {
  if (inProgress !== undefined) {
    return;
  }
  const guid = internalQueue.shift();
  if (guid === undefined) {
    return;
  }

  inProgress = guid;
  const currentStatus = getCheckingQueue(guid);
  const cancel = currentStatus?.stage === 'queued' ? currentStatus.cancel : () => {};
  setCheckingQueue(guid, {
    stage: 'inprogress',
    guid,
    cancel,
  });

  runCheck(guid);
}

function runCheck(guid: string): void {
  apiCall('POST /check-nzb', {id: guid})
    .then(res => {
      inProgress = undefined;
      if (inProgressCanceled === guid) {
        inProgressCanceled = undefined;
        processQueue();
        return;
      }
      const checkResult = res as NzbCheckResult;
      setCheckingQueue(guid, {stage: 'success', guid, checkResult});
      const current = getNzbsu(guid);
      if (current) {
        setNzbsu(guid, {...current, lastCheck: checkResult});
      }
      processQueue();
    })
    .catch(err => {
      if (inProgressCanceled === guid) {
        inProgress = undefined;
        inProgressCanceled = undefined;
        processQueue();
        return;
      }

      const blockedByLock = err instanceof Error && err.message.includes(BUSY_ERROR_MESSAGE);
      if (blockedByLock) {
        showBusyCheckAlert();
      }

      inProgress = undefined;
      setCheckingQueue(guid, {stage: 'failed', guid, err});
      if (blockedByLock) {
        for (const queuedGuid of internalQueue.splice(0)) {
          setCheckingQueue(queuedGuid, {stage: 'failed', guid: queuedGuid, err});
        }
        return;
      }
      processQueue();
    });
}

function showBusyCheckAlert(): void {
  const now = Date.now();
  if (now - lastBusyAlertAt < BUSY_ALERT_COOLDOWN_MS) {
    return;
  }
  lastBusyAlertAt = now;
  showAlert(
    'Another NZB check is running. Turn off the Newsgroup toggle to stop new automatic checks. ' +
      'Wait for the current run to finish, then click retry.',
    {duration: 10_000},
  );
}

export function queueCheck(guid: string): void {
  const currentStatus = getCheckingQueue(guid);
  if (currentStatus?.stage === 'queued' || currentStatus?.stage === 'inprogress') {
    return;
  }

  internalQueue.push(guid);

  function cancel(): void {
    const index = internalQueue.indexOf(guid);
    if (index !== -1) {
      internalQueue.splice(index, 1);
    }
    if (inProgress === guid) {
      inProgressCanceled = guid;
    }
  }

  setCheckingQueue(guid, {stage: 'queued', guid, cancel});
  processQueue();
}
